import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMobileAuth } from '../MobileAuthProvider';
import { getOfflineActions } from '../utils/offlineStorage';
import { queueOrSend } from '../utils/syncQueue';
import { apiErrorMessage } from '../../utils/apiError';
import { authHeaders } from '../../utils/authHeaders';
import { tone } from '../utils/tone';
import { apiBase } from '../../utils/authHeaders';
import { 
  Thermometer, CheckCircle, AlertTriangle, Clock, FileText
} from 'lucide-react';

const MobileSafety = () => {
  const location = useLocation();
  const { token, isOnline } = useMobileAuth();
  const [activeTab, setActiveTab] = useState('temperature');
  const [temperatureLogs, setTemperatureLogs] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [offlineActions, setOfflineActions] = useState([]);
  const [showIncidentModal, setShowIncidentModal] = useState(false);
  const [incidentDraft, setIncidentDraft] = useState({ type: '', description: '', severity: 'medium', location: '' });
  const [feedback, setFeedback] = useState(null);

  const fetchSafetyData = useCallback(async () => {
    try {
      const headers = authHeaders({ token, locationId: localStorage.getItem('current_location_id') });
      
      switch (activeTab) {
        case 'temperature':
          const tempResponse = await fetch(`${apiBase()}/api/mobile/safety/temperature`, {
            headers
          });
          if (tempResponse.ok) setTemperatureLogs(await tempResponse.json());
          break;
          
        case 'checklists':
          const checklistResponse = await fetch(`${apiBase()}/api/mobile/safety/checklists`, {
            headers
          });
          if (checklistResponse.ok) setChecklists(await checklistResponse.json());
          break;
          
        case 'incidents':
          const incidentResponse = await fetch(`${apiBase()}/api/mobile/safety/incidents`, {
            headers
          });
          if (incidentResponse.ok) setIncidents(await incidentResponse.json());
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Error fetching safety data:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTab, token]);

  const loadOfflineActions = async () => {
    try {
      const actions = await getOfflineActions();
      setOfflineActions(actions.filter(action => action.queueType === 'safety' || action.type === 'safety'));
    } catch (error) {
      console.error('Error loading offline actions:', error);
    }
  };

  const completeChecklistItem = async (checklistId, itemId, response, notes = '') => {
    setFeedback(null);
    const result = await queueOrSend({
      method: 'POST',
      path: '/api/mobile/safety/checklist/complete',
      body: { checklistId, itemId, response, notes },
      requiresScope: true,
      queueType: 'safety',
    });
    if (result.ok) fetchSafetyData();
    else if (result.queued) {
      setFeedback({ kind: 'queued', msg: 'Checklist response saved on this device and queued to sync.' });
      loadOfflineActions();
    } else setFeedback({ kind: 'error', msg: apiErrorMessage(result.error, 'Could not complete checklist item.') });
  };

  useEffect(() => {
    fetchSafetyData();
    loadOfflineActions();
  }, [fetchSafetyData]);

  useEffect(() => {
    if (location.state?.tab) setActiveTab(location.state.tab);
  }, [location.state]);

  const reportIncident = async (incidentData) => {
    setFeedback(null);
    const result = await queueOrSend({
      method: 'POST',
      path: '/api/mobile/safety/incident/report',
      body: incidentData,
      requiresScope: true,
      queueType: 'safety',
    });
    if (result.ok) {
      setFeedback({ kind: 'ok', msg: 'Incident report recorded.' });
      setIncidentDraft({ type: '', description: '', severity: 'medium', location: '' });
      setShowIncidentModal(false);
      fetchSafetyData();
    }
    else if (result.queued) {
      setFeedback({ kind: 'queued', msg: 'Incident report saved on this device and queued to sync.' });
      loadOfflineActions();
      setIncidentDraft({ type: '', description: '', severity: 'medium', location: '' });
      setShowIncidentModal(false);
    } else setFeedback({ kind: 'error', msg: apiErrorMessage(result.error, 'Could not report incident.') });
  };

  const getTemperatureStatus = (log) => {
    if (!Number.isFinite(log.min_safe_temperature)
      || !Number.isFinite(log.max_safe_temperature)
      || !log.unit
      || log.safe_temperature_unit !== log.unit) {
      return { status: 'not assessed', color: 'gray', icon: Clock };
    }
    if (log.temperature < log.min_safe_temperature || log.temperature > log.max_safe_temperature) {
      return { status: 'unsafe', color: 'red', icon: AlertTriangle };
    }
    return { status: 'safe', color: 'green', icon: CheckCircle };
  };

  const TemperatureTab = () => (
    <div className="space-y-4">
      {/* Temperature values must be entered from an actual measurement. */}
      <div className="bg-surface rounded-lg p-4 shadow-sm">
        <h3 className="font-semibold text-ink mb-2">Record a measured temperature</h3>
        <p className="text-sm text-ink-muted">Enter the equipment and reading you measured. No site equipment limits are configured here.</p>
        <Link to="/mobile/boh/temperature" className="mt-3 inline-flex min-h-11 items-center gap-2 border border-primary-600 px-4 text-sm font-medium text-primary-800">
          <Thermometer aria-hidden="true" className="h-4 w-4" /> Enter reading
        </Link>
      </div>

      {/* Recent Logs */}
      <div className="bg-surface rounded-lg p-4 shadow-sm">
        <h3 className="font-semibold text-ink mb-3">Recent Logs</h3>
        <div className="space-y-3">
          {temperatureLogs.map(log => {
            const tempStatus = getTemperatureStatus(log);
            const StatusIcon = tempStatus.icon;
            
            return (
              <div key={log.id} className="flex items-center justify-between p-3 bg-surface-muted rounded-lg">
                <div className="flex items-center space-x-3">
                  <StatusIcon className={`w-5 h-5 ${tone(tempStatus.color).icon}`} />
                  <div>
                    <p className="font-medium text-ink">{log.equipment_name || log.equipment_id}</p>
                    <p className="text-sm text-ink-muted">{new Date(log.timestamp).toLocaleTimeString()}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`font-bold ${tone(tempStatus.color).strong}`}>
                    {log.temperature}°{log.unit || 'C'}
                  </p>
                  <p className="text-xs text-ink-muted">
                    {tempStatus.status === 'not assessed'
                      ? 'No unit-matched safe range'
                      : `${log.min_safe_temperature}° - ${log.max_safe_temperature}°`}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  const ChecklistsTab = () => (
    <div className="space-y-4">
      {/* Active Checklists */}
      <div className="bg-surface rounded-lg p-4 shadow-sm">
        <h3 className="font-semibold text-ink mb-3">Active Checklists</h3>
        <div className="space-y-3">
          {checklists.map(checklist => (
            <div key={checklist.id} className="border border-surface-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-ink">{checklist.name}</h4>
                <span className={`px-2 py-1 rounded-full text-xs ${
                  checklist.status === 'completed' ? 'bg-primary-100 text-primary-800' :
                  checklist.status === 'in_progress' ? 'bg-warn/15 text-accent-800' :
                  'bg-surface-sunken text-ink'
                }`}>
                  {checklist.status.replace('_', ' ')}
                </span>
              </div>
              
              <div className="space-y-2">
                {checklist.items.slice(0, 3).map(item => (
                  <div key={item.id} className="flex items-center justify-between">
                    <span className="text-sm text-ink">{item.question}</span>
                    <button
                      onClick={() => completeChecklistItem(checklist.id, item.id, 'yes')}
                      className={`px-3 py-1 rounded text-xs ${
                        item.completed ? 'bg-primary-100 text-primary-800' : 'bg-surface-sunken text-ink-muted'
                      }`}
                    >
                      {item.completed ? 'Done' : 'Complete'}
                    </button>
                  </div>
                ))}
              </div>
              
              {checklist.items.length > 3 && (
                <button className="text-sm text-primary-600 mt-2">View all items →</button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const IncidentsTab = () => (
    <div className="space-y-4">
      {/* Report Incident Button */}
      <button
        type="button"
        onClick={() => setShowIncidentModal(true)}
        className="w-full flex items-center justify-center space-x-2 p-4 bg-danger text-white rounded-lg"
      >
        <AlertTriangle className="w-5 h-5" />
        <span>Report Incident</span>
      </button>

      {showIncidentModal && (
        <form
          aria-label="Report safety incident"
          className="space-y-3 border border-surface-border bg-surface p-4"
          onSubmit={(event) => {
            event.preventDefault();
            reportIncident(incidentDraft);
          }}
        >
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-ink">Incident details</h3>
            <button type="button" aria-label="Close incident form" onClick={() => setShowIncidentModal(false)} className="p-2">Close</button>
          </div>
          <label className="block text-sm text-ink">Incident type
            <input required maxLength="120" value={incidentDraft.type} onChange={(event) => setIncidentDraft((draft) => ({ ...draft, type: event.target.value }))} className="mt-1 block min-h-11 w-full border border-surface-border px-3" />
          </label>
          <label className="block text-sm text-ink">What happened?
            <textarea required maxLength="4000" value={incidentDraft.description} onChange={(event) => setIncidentDraft((draft) => ({ ...draft, description: event.target.value }))} rows={4} className="mt-1 block w-full border border-surface-border px-3 py-2" />
          </label>
          <label className="block text-sm text-ink">Severity
            <select value={incidentDraft.severity} onChange={(event) => setIncidentDraft((draft) => ({ ...draft, severity: event.target.value }))} className="mt-1 block min-h-11 w-full border border-surface-border px-3">
              <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option>
            </select>
          </label>
          <label className="block text-sm text-ink">Location (optional)
            <input maxLength="160" value={incidentDraft.location} onChange={(event) => setIncidentDraft((draft) => ({ ...draft, location: event.target.value }))} className="mt-1 block min-h-11 w-full border border-surface-border px-3" />
          </label>
          <button type="submit" className="min-h-11 w-full bg-danger px-4 text-sm font-medium text-white">Save incident report</button>
        </form>
      )}

      {/* Recent Incidents */}
      <div className="bg-surface rounded-lg p-4 shadow-sm">
        <h3 className="font-semibold text-ink mb-3">Recent Incidents</h3>
        <div className="space-y-3">
          {incidents.map(incident => (
            <div key={incident.id} className="border border-surface-border rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-medium text-ink">{incident.type}</h4>
                  <p className="text-sm text-ink-muted mt-1">{incident.description}</p>
                  <p className="text-xs text-ink-muted mt-2">
                    {new Date(incident.reportedAt).toLocaleDateString()}
                  </p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs ${
                  incident.severity === 'critical' ? 'bg-danger/10 text-danger' :
                  incident.severity === 'high' ? 'bg-accent-100 text-accent-800' :
                  'bg-warn/15 text-accent-800'
                }`}>
                  {incident.severity}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-surface-muted">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-muted">
      {/* Header */}
      <div className="bg-surface shadow-sm sticky top-0 z-10">
        <div className="px-4 py-3">
          <h1 className="font-display text-xl font-bold text-ink">Safety & Compliance</h1>
          <p className="text-sm text-ink-muted">Maintain safety standards</p>
        </div>
        
        {/* Tab Navigation */}
        <div className="flex border-b border-surface-border">
          {[
            { id: 'temperature', label: 'Temperature', icon: Thermometer },
            { id: 'checklists', label: 'Checklists', icon: FileText },
            { id: 'incidents', label: 'Incidents', icon: AlertTriangle }
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center space-x-2 py-3 border-b-2 ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-ink-muted'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-sm font-medium">{tab.label}</span>
              </button>
            );
          })}
        </div>
        <div className="border-b border-surface-border px-4 py-2">
          <Link
            to="/mobile/safety/inspections"
            className="inline-flex min-h-10 items-center gap-2 text-sm font-medium text-primary-700"
          >
            <FileText aria-hidden="true" className="h-4 w-4" />
            Inspection evidence
          </Link>
        </div>
      </div>

      {/* Offline Sync Status */}
      {!isOnline && offlineActions.length > 0 && (
        <div className="bg-warn/10 border-b border-warn/40 px-4 py-2">
          <div className="flex items-center justify-between">
            <p className="text-sm text-accent-800">
              {offlineActions.length} safety logs will sync when online
            </p>
            <button className="text-sm text-accent-600 underline">
              Sync Now
            </button>
          </div>
        </div>
      )}

      {/* Action Feedback (e.g. a rejected/failed log — never fail silently) */}
      {feedback && (
        <div className={`border-b px-4 py-2 ${feedback.kind === 'error' ? 'bg-danger/5 border-danger/30 text-danger' : 'bg-primary-50 border-primary-200 text-primary-800'}`} role={feedback.kind === 'error' ? 'alert' : 'status'} data-testid={`safety-feedback-${feedback.kind}`}>
          <p className="text-sm">{feedback.msg}</p>
        </div>
      )}

      {/* Tab Content */}
      <div className="px-4 py-4">
        {activeTab === 'temperature' && <TemperatureTab />}
        {activeTab === 'checklists' && <ChecklistsTab />}
        {activeTab === 'incidents' && <IncidentsTab />}
      </div>

    </div>
  );
};

export default MobileSafety;
